import { useCallback, useEffect, useState } from "react";
import { isAxiosError } from "axios";
import { profileClient } from "../../api/clients";
import { ReportGenUser } from "../interfaces/User";
import { fetchUsers } from "../utils/fetchUsers";
import { useErrorSnackbar } from "./useErrorSnackbar";

export const useStationUsers = (organizationId: string) => {
  const [users, setUsers] = useState<ReportGenUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [resendingInviteEmail, setResendingInviteEmail] = useState<string | null>(null);
  const [resettingPasswordEmail, setResettingPasswordEmail] = useState<string | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusAction, setStatusAction] = useState<"activate" | "deactivate" | null>(null);
  const [selectedUser, setSelectedUser] = useState<ReportGenUser | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const { showError, showSuccess, handleCloseSnackbar, snackBarMessage, snackBarMode } =
    useErrorSnackbar();

  const fetchAllUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const fetched = await fetchUsers(organizationId, true);
      setUsers(fetched);
    } catch (error) {
      const message = isAxiosError(error) ? error.response?.data?.error : null;
      showError(message || "Failed to fetch users");
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, showError]);

  useEffect(() => {
    if (!organizationId) return;
    let canceled = false;
    setIsLoading(true);
    fetchUsers(organizationId, true)
      .then((fetched) => {
        if (canceled) return;
        setUsers(fetched);
      })
      .catch((error) => {
        if (canceled) return;
        const message = isAxiosError(error) ? error.response?.data?.error : null;
        showError(message || "Failed to fetch users");
      })
      .finally(() => {
        if (!canceled) setIsLoading(false);
      });
    return () => {
      canceled = true;
    };
  }, [organizationId, showError]);

  const handleResendInvite = useCallback(
    async (user: ReportGenUser) => {
      setResendingInviteEmail(user.email);
      try {
        if (user.id) {
          await profileClient.post(
            `/v1/stations/${organizationId}/users/${user.id}/invite`,
            { userId: user.id },
          );
        } else {
          await profileClient.post(
            `/v1/stations/${organizationId}/users/invite`,
            { email: user.email },
          );
        }
        showSuccess(`Invitation resent to ${user.email}`);
      } catch (error) {
        const message = isAxiosError(error) ? error.response?.data?.error : null;
        showError(message || "Failed to resend invitation");
      } finally {
        setResendingInviteEmail(null);
      }
    },
    [organizationId, showError, showSuccess],
  );

  const handleResetPassword = useCallback(
    async (user: ReportGenUser) => {
      if (!user.id) {
        showError("Cannot reset password: User ID is missing.");
        return;
      }
      setResettingPasswordEmail(user.email);
      try {
        await profileClient.post(
          `/v1/stations/${organizationId}/users/${user.id}/password-reset`,
        );
        showSuccess(`Password reset email sent to ${user.email}`);
      } catch (error) {
        const message = isAxiosError(error) ? error.response?.data?.error : null;
        showError(message || "Failed to send password reset");
      } finally {
        setResettingPasswordEmail(null);
      }
    },
    [organizationId, showError, showSuccess],
  );

  const handleStatusChangeRequest = useCallback(
    (user: ReportGenUser, action: "activate" | "deactivate") => {
      setSelectedUser(user);
      setStatusAction(action);
      setStatusDialogOpen(true);
    },
    [],
  );

  const closeStatusDialog = useCallback(() => {
    if (isUpdatingStatus) return;
    setStatusDialogOpen(false);
    setStatusAction(null);
    setSelectedUser(null);
  }, [isUpdatingStatus]);

  const handleStatusConfirm = useCallback(async () => {
    if (!selectedUser || !statusAction) return;
    setIsUpdatingStatus(true);
    try {
      const payload = { email: selectedUser.email };
      if (statusAction === "activate") {
        await profileClient.post(
          `/v1/stations/${organizationId}/users/${selectedUser.id}/activate`,
          payload,
        );
        showSuccess("User re-activated successfully.");
      } else {
        await profileClient.post(
          `/v1/stations/${organizationId}/users/${selectedUser.id}/deactivate`,
          payload,
        );
        showSuccess("User deactivated successfully.");
      }
      await fetchAllUsers();
    } catch (error) {
      const message = isAxiosError(error) ? error.response?.data?.error : null;
      showError(message || "Failed to update user status.");
    } finally {
      setIsUpdatingStatus(false);
      setStatusDialogOpen(false);
      setStatusAction(null);
      setSelectedUser(null);
    }
  }, [selectedUser, statusAction, organizationId, showError, showSuccess, fetchAllUsers]);

  const statusUpdatingEmail = isUpdatingStatus && selectedUser ? selectedUser.email : null;

  return {
    users,
    isLoading,
    searchTerm,
    setSearchTerm,
    resendingInviteEmail,
    resettingPasswordEmail,
    statusUpdatingEmail,
    statusDialogOpen,
    statusAction,
    selectedUser,
    isUpdatingStatus,
    snackBarMessage,
    snackBarMode,
    handleCloseSnackbar,
    showError,
    showSuccess,
    refresh: fetchAllUsers,
    handleResendInvite,
    handleResetPassword,
    handleStatusChangeRequest,
    closeStatusDialog,
    handleStatusConfirm,
  };
};
